// ─────────────────────────────────────────────────────────────────────────────
// Patient Repository - TypeScript Implementation
// Specific repository for patient entity operations with type safety and sync logic
// ─────────────────────────────────────────────────────────────────────────────

import { createRepo } from "./createRepo";
import { Patient, RepositoryOptions, PaginatedResponse } from "../../types";
import { mergeStructuredHistory } from "../../utils/medicalHistoryDraft";
import { normalizeDOBForStorage } from "../../utilis/dateTime";

/**
 * Force DateOfBirth to the single stored format, "YYYY-MM-DD".
 *
 * Applied on every write so no caller can store anything else. Callers used to
 * disagree — the patient form stored midnight-UTC datetimes, the sedation
 * workflow stored the raw form value, the sync mappers stored bare dates — and
 * because duplicate detection compares DateOfBirth as an exact string, two
 * formats for the same birthday read as two different patients.
 *
 * Records written before this converge the first time they are touched.
 */
function withNormalisedDob<T extends Partial<Patient>>(data: T): T {
  if (!data || !("DateOfBirth" in data)) return data;
  return { ...data, DateOfBirth: normalizeDOBForStorage(data.DateOfBirth) };
}

// Create typed patient repository instance with outbox disabled
export const patientRepo = createRepo<Patient>({
  storeName: "patients",
  entityName: "Patient",
  skipOutbox: true, // Skip automatic outbox - patients are synced manually at the end
});

// Convenience methods for patient-specific operations with full type safety
export const getPatients = (
  options: RepositoryOptions = {}
): Promise<PaginatedResponse<Patient>> => patientRepo.listPaged(options);

export const getPatientById = (id: string): Promise<Patient | null> =>
  patientRepo.getById(id);

export const createPatient = (
  data: Omit<Patient, "Id" | "CreatedDateTime">
): Promise<Patient> => patientRepo.create(withNormalisedDob(data));

export const updatePatient = (
  id: string,
  data: Partial<Patient>
): Promise<Patient> => patientRepo.update(id, withNormalisedDob(data));

export const deletePatient = (id: string): Promise<boolean> =>
  patientRepo.softDelete(id);

// Enhanced upsert function that prevents patient duplicates (Requirement 2)
export const upsertPatientWithDuplicateCheck = async (
  patientData: Partial<Patient>
): Promise<{ patient: Patient; isNewCreation: boolean }> => {
  // If we have an ID, try to update existing
  if (patientData.Id) {
    const existingPatient = await getPatientById(patientData.Id);
    if (existingPatient) {
      const updatedPatient = await updatePatient(patientData.Id, {
        ...patientData,
        MedicalHistory: smartMergeMedicalField(existingPatient.MedicalHistory, patientData.MedicalHistory),
        Allergies: smartMergeMedicalField(existingPatient.Allergies, patientData.Allergies),
        Medications: smartMergeMedicalField(existingPatient.Medications, patientData.Medications),
        AnestheticHistory: smartMergeMedicalField(existingPatient.AnestheticHistory, patientData.AnestheticHistory),
        ModifiedDateTime: new Date().toISOString()
      });
      return { patient: updatedPatient, isNewCreation: false };
    }
  }

  // Check for existing patient by name and DOB to prevent duplicates
  if (patientData.FirstName && patientData.LastName && patientData.DateOfBirth) {
    const allPatients = await patientRepo.listAll();
    
    // Check for existing patients with same name and DOB
    const duplicatePatients = allPatients.filter(p => 
      p.FirstName?.toLowerCase() === patientData.FirstName?.toLowerCase() &&
      p.LastName?.toLowerCase() === patientData.LastName?.toLowerCase() &&
      normalizeDOBForStorage(p.DateOfBirth) === normalizeDOBForStorage(patientData.DateOfBirth) &&
      !p.Deactivated
    );

    if (duplicatePatients.length > 0) {
      // Update the existing patient instead of creating a new one
      const existingPatient = duplicatePatients[0];
      const updatedPatient = await updatePatient(existingPatient.Id, {
        ...existingPatient,
        ...patientData,
        MedicalHistory: smartMergeMedicalField(existingPatient.MedicalHistory, patientData.MedicalHistory),
        Allergies: smartMergeMedicalField(existingPatient.Allergies, patientData.Allergies),
        Medications: smartMergeMedicalField(existingPatient.Medications, patientData.Medications),
        AnestheticHistory: smartMergeMedicalField(existingPatient.AnestheticHistory, patientData.AnestheticHistory),
        Id: existingPatient.Id, // Ensure we keep the existing ID
        ModifiedDateTime: new Date().toISOString()
      });
      return { patient: updatedPatient, isNewCreation: false };
    }
  }

  // No existing patient found, create new one
  const newPatient = await createPatient(patientData as Omit<Patient, "Id" | "CreatedDateTime">);
  return { patient: newPatient, isNewCreation: true };
};

/**
 * Merge one legacy medical-history column (MedicalHistory, Allergies,
 * Medications, AnestheticHistory) line by line.
 *
 * Line by line, not blob by blob: the API re-renders this column from the
 * patient pack, so one reworded or reordered answer made the whole re-render
 * count as "new" text and the old code appended a near-duplicate of everything
 * on every sync. That is how a patient ended up with the same block of answers
 * printed three times in the PDF.
 */
function smartMergeMedicalField(localValue: string | null | undefined, apiValue: string | null | undefined): string | null {
  // If no API value, keep local value
  if (!apiValue || apiValue.trim() === '') {
    return localValue || null;
  }

  // If no local value, use API value
  if (!localValue || localValue.trim() === '') {
    return apiValue;
  }

  const seen = new Set(localValue.split('\n').map(medicalLineKey).filter(Boolean));

  const additions = apiValue.split('\n').filter((line) => {
    const key = medicalLineKey(line);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  if (additions.length === 0) return localValue;

  return `${localValue.trim()}\n${additions.map((l) => l.trim()).join('\n')}`;
}

/** Two lines are the same answer if only case, spacing or trailing punctuation differ. */
function medicalLineKey(line: string): string {
  return line.trim().toLowerCase().replace(/[.,;]+$/, '');
}

/**
 * Simple merge for physical measurements (Height, Weight, DOB)
 * - Only update if local value is empty or 0
 * - Otherwise, keep local value (don't overwrite existing measurements)
 */
const isRecordedUsage = (status?: string | null): boolean => status === 'Yes' || status === 'Former';

const mergeYes = (local?: boolean | null, api?: boolean | null): boolean | null =>
  local === true || api === true ? true : local ?? api ?? null;

function mergeUsageStatus(localStatus?: string | null, apiStatus?: string | null): string | undefined {
  if (isRecordedUsage(localStatus)) return localStatus!;
  if (isRecordedUsage(apiStatus)) return apiStatus!;
  return (localStatus && localStatus !== 'None' ? localStatus : apiStatus) ?? undefined;
}

function smartMergePhysicalMeasurement(
  localValue: number | string | null | undefined,
  apiValue: number | string | null | undefined,
  isDate: boolean = false
): number | string | null | undefined {
  // If local has a meaningful value, keep it
  if (localValue !== null && localValue !== undefined && localValue !== 0 && localValue !== '' && localValue !== '0') {
    return localValue;
  }
  
  // Local is empty/zero - use API value if it's meaningful
  if (apiValue !== null && apiValue !== undefined && apiValue !== 0 && apiValue !== '' && apiValue !== '0') {
    // For dates, validate before using
    if (isDate && typeof apiValue === 'string') {
      const apiDate = new Date(apiValue);
      if (isNaN(apiDate.getTime())) {
        return localValue; // Invalid date, keep local
      }
    }
    return apiValue;
  }
  
  // Both are empty - keep local
  return localValue;
}

// Sync-specific patient operations
export const upsertPatient = async (patientData: Partial<Patient>): Promise<Patient> => {
  // This path writes through patientRepo directly rather than the wrappers
  // above, so it normalises its own input.
  patientData = withNormalisedDob(patientData);

  if (!patientData.Id) {
    // Create new patient
    return await patientRepo.create(patientData as Omit<Patient, "Id" | "CreatedDateTime">);
  }

  // Check if patient exists locally
  const existingPatient = await patientRepo.getById(patientData.Id);

  if (existingPatient) {
    // If there are local unsynced changes, avoid overwriting to prevent data loss
    if (existingPatient.PendingSync && existingPatient.PendingSync !== null) {

      return await patientRepo.update(patientData.Id, {
        Synced: false, // keep unsynced flag
        LastSyncedAt: new Date().toISOString(),
      });
    }

    // Apply smart merge for all updates (no timestamp comparison needed)
    // Smart merge for medical history fields
    const mergedMedicalHistory = smartMergeMedicalField(existingPatient.MedicalHistory, patientData.MedicalHistory);
    const mergedAllergies = smartMergeMedicalField(existingPatient.Allergies, patientData.Allergies);
    const mergedMedications = smartMergeMedicalField(existingPatient.Medications, patientData.Medications);
    const mergedAnestheticHistory = smartMergeMedicalField(existingPatient.AnestheticHistory, patientData.AnestheticHistory);
    const mergedMedicalHistoryFields = mergeStructuredHistory(
      existingPatient.MedicalHistoryFields,
      patientData.MedicalHistoryFields
    );
    
    // Smart merge for physical measurements
    const mergedHeight = smartMergePhysicalMeasurement(existingPatient.Height, patientData.Height) as number | null;
    const mergedWeight = smartMergePhysicalMeasurement(existingPatient.Weight, patientData.Weight) as number | null;
    const mergedDateOfBirth = smartMergePhysicalMeasurement(existingPatient.DateOfBirth, patientData.DateOfBirth, true) as string | null;
    
    // Smart merge for Risk Assessment fields - CRITICAL: Preserve locally captured data
    const mergedASAClassification = smartMergePhysicalMeasurement(existingPatient.ASAClassification, patientData.ASAClassification) as number | undefined;
    const mergedLastMealAgoInHours = smartMergePhysicalMeasurement(existingPatient.LastMealAgoInHours, patientData.LastMealAgoInHours) as number | null;
    const mergedLastFluidAgoInHours = smartMergePhysicalMeasurement(existingPatient.LastFluidAgoInHours, patientData.LastFluidAgoInHours) as number | null;
    
    // Preserve smoking/alcohol status only when it says something (Yes/Former). "Never" is
    // also the form default, so it must not block a patient-pack Yes/Former — that left the
    // status at Never with the pack's note hidden behind it (S-04).
    const mergedSmokingStatus = mergeUsageStatus(existingPatient.SmokingStatus, patientData.SmokingStatus);
    const mergedAlcoholStatus = mergeUsageStatus(existingPatient.AlcoholStatus, patientData.AlcoholStatus);
    
    // Preserve smoking/alcohol notes using smart merge
    const mergedSmokingNote = smartMergeMedicalField(existingPatient.SmokingNote, patientData.SmokingNote);
    const mergedAlcoholNote = smartMergeMedicalField(existingPatient.AlcoholNote, patientData.AlcoholNote);
    
    // Merge height/weight formats only if the measurement itself was updated
    const mergedHeightFormat = mergedHeight === patientData.Height && patientData.HeightFormat 
      ? patientData.HeightFormat 
      : existingPatient.HeightFormat;
    const mergedWeightFormat = mergedWeight === patientData.Weight && patientData.WeightFormat 
      ? patientData.WeightFormat 
      : existingPatient.WeightFormat;

    // Create merged object - CRITICAL: Only update fields that are safe to update
    // Protected fields (smart-merged): These are extracted and ignored from API data
    // We'll use our smart-merged values instead
    const {
      // Medical history fields - will be smart-merged
      MedicalHistory: apiMedicalHistory,
      Allergies: apiAllergies,
      Medications: apiMedications,
      AnestheticHistory: apiAnestheticHistory,
      // Structured history - merged separately, like the four fields above.
      // (What actually protects it is the explicit assignment in `merged`,
      // which comes after the ...safeApiFields spread and so wins regardless.
      // Pulling it out here keeps safeApiFields to genuinely safe fields.)
      MedicalHistoryFields: apiMedicalHistoryFields,
      // Physical measurements - will be smart-merged
      Height: apiHeight, 
      Weight: apiWeight, 
      DateOfBirth: apiDateOfBirth, 
      HeightFormat: apiHeightFormat, 
      WeightFormat: apiWeightFormat,
      // Risk Assessment fields - will be smart-merged
      ASAClassification: apiASAClassification,
      SmokingStatus: apiSmokingStatus,
      AlcoholStatus: apiAlcoholStatus,
      LastMealAgoInHours: apiLastMealAgoInHours,
      LastFluidAgoInHours: apiLastFluidAgoInHours,
      SmokingNote: apiSmokingNote,
      AlcoholNote: apiAlcoholNote,
      AirwayDifficultyAnticipated: apiAirwayDifficultyAnticipated,
      AirwayNote: apiAirwayNote,
      RecreationalDrugUse: apiRecreationalDrugUse,
      RecreationalDrugNote: apiRecreationalDrugNote,
      Vapes: apiVapes,
      // Everything else is safe to update from API
      ...safeApiFields 
    } = patientData;

    // Build final merged object with correct priority:
    // 1. Start with existing local data (baseline)
    // 2. Apply safe API updates (fields that don't accumulate)
    // 3. Override with smart-merged protected fields (preserve local additions)
    const merged: Partial<Patient> = {
      ...existingPatient,   // Start with all existing data
      ...safeApiFields,     // Update non-protected fields from API (Name, Email, Phone, etc.)
      // Override with smart-merged protected fields (these take precedence)
      MedicalHistory: mergedMedicalHistory,
      Allergies: mergedAllergies,
      Medications: mergedMedications,
      AnestheticHistory: mergedAnestheticHistory,
      MedicalHistoryFields: mergedMedicalHistoryFields,
      Height: mergedHeight,
      Weight: mergedWeight,
      DateOfBirth: normalizeDOBForStorage(mergedDateOfBirth),
      HeightFormat: mergedHeightFormat,
      WeightFormat: mergedWeightFormat,
      // Risk Assessment fields (preserve local data)
      ASAClassification: mergedASAClassification,
      SmokingStatus: mergedSmokingStatus,
      AlcoholStatus: mergedAlcoholStatus,
      LastMealAgoInHours: mergedLastMealAgoInHours,
      LastFluidAgoInHours: mergedLastFluidAgoInHours,
      SmokingNote: mergedSmokingNote,
      AlcoholNote: mergedAlcoholNote,
      // Airway assessment (S-16): the clinician's local answer wins; the API fills a gap
      AirwayDifficultyAnticipated: existingPatient.AirwayDifficultyAnticipated ?? apiAirwayDifficultyAnticipated ?? null,
      AirwayNote: existingPatient.AirwayNote ?? apiAirwayNote ?? null,
      // Recreational drugs / vaping (S-19): a Yes from either side wins, so a patient pack answer
      // isn't hidden behind an older local No (the S-04 smoking bug).
      RecreationalDrugUse: mergeYes(existingPatient.RecreationalDrugUse, apiRecreationalDrugUse),
      RecreationalDrugNote: smartMergeMedicalField(existingPatient.RecreationalDrugNote, apiRecreationalDrugNote),
      Vapes: mergeYes(existingPatient.Vapes, apiVapes),
      // Sync metadata
      Synced: true,
      LastSyncedAt: new Date().toISOString(),
      PendingSync: null,
    };
    return await patientRepo.update(patientData.Id, merged);
  } else {
    return await patientRepo.create({
      ...patientData,
      Synced: true,
      LastSyncedAt: new Date().toISOString(),
      PendingSync: null,
    } as Omit<Patient, "Id" | "CreatedDateTime">);
  }
};

export const markPatientForSync = async (
  id: string, 
  operation: 'create' | 'update' | 'delete'
): Promise<void> => {
  await patientRepo.update(id, {
    Synced: false,
    PendingSync: operation,
    ModifiedDateTime: new Date().toISOString(),
  });
};

export const getPendingSyncPatients = async (): Promise<Patient[]> => {
  const allPatients = await patientRepo.listAll();
  return allPatients.filter(patient => 
    patient.PendingSync && !patient.Synced
  );
};

// NEW: Force update from API (for manual sync operations only)
export const forceUpdatePatientFromApi = async (patientData: Partial<Patient>): Promise<Patient> => {
  if (!patientData.Id) {
    throw new Error("Patient ID is required for force update");
  }

  // Writes through patientRepo directly, so it normalises its own input.
  patientData = withNormalisedDob(patientData);

  // Check if patient exists locally
  const existingPatient = await patientRepo.getById(patientData.Id);
  
  if (existingPatient) {
    // Force update existing patient with API data (use with caution!)
    const updatedData = {
      ...existingPatient, // Start with existing data
      ...patientData,    // Override with new API data
      Synced: true,
      LastSyncedAt: new Date().toISOString(),
      PendingSync: null,
    };
    return await patientRepo.update(patientData.Id, updatedData);
  } else {
    // Create new patient with API data
    return await patientRepo.create({
      ...patientData,
      Synced: true,
      LastSyncedAt: new Date().toISOString(),
      PendingSync: null,
    } as Omit<Patient, "Id" | "CreatedDateTime">);
  }
};



// Helper to build a Patient object with TypeScript type safety
// C#-aligned property names and default values
export function buildPatient(partial: Partial<Patient> = {}): Partial<Patient> {
  const defaultPatient: Partial<Patient> = {
    FirstName: "",
    LastName: "",
    Title: "GeneralDentistry", // string enums recommended
    DateOfBirth: null, // ISO string preferred
    Age: 0, // server can recompute; kept for UI convenience
    Address: null,
    Town: null,
    Country: null,
    PostCode: null,
    PhoneNumber: null,
    AlternativePhoneNumber: null,
    BusinessName: null,
    Email: null,
    MedicalHistory: null,
    Allergies: null,
    Medications: null,
    AnestheticHistory: null,
    ASAClassification: 0,
    SmokingStatus: "None",
    AlcoholStatus: "None",
    LastMealAgoInHours: null,
    LastFluidAgoInHours: null,
    Occupation: null,
    HeightFormat: "cm",
    Height: null,
    WeightFormat: "kg",
    Weight: null,
    TicketId: null,
    Notes: null,
    SmokingNote: null,
    AlcoholNote: null,
    AirwayDifficultyAnticipated: null,
    AirwayNote: null,
    RecreationalDrugUse: null,
    RecreationalDrugNote: null,
    Vapes: null,
    BMI: 0,
  };

  return {
    ...defaultPatient,
    ...partial,
  };
}

// Additional patient-specific query methods with type safety
export const getPatientsByLastName = async (
  lastName: string
): Promise<Patient[]> => {
  const allPatients = await patientRepo.listAll();
  return allPatients.filter(
    (patient) =>
      patient.LastName &&
      patient.LastName.toLowerCase() === lastName.toLowerCase()
  );
};

export const getPatientsByEmail = async (email: string): Promise<Patient[]> => {
  const allPatients = await patientRepo.listAll();
  return allPatients.filter(
    (patient) =>
      patient.Email && patient.Email.toLowerCase() === email.toLowerCase()
  );
};

export const getActivePatients = async (
  options: RepositoryOptions = {}
): Promise<PaginatedResponse<Patient>> => {
  return patientRepo.listPaged({
    ...options,
    includeDeactivated: false,
  });
};

export const searchPatientsByName = async (
  searchTerm: string
): Promise<Patient[]> => {
  const allPatients = await patientRepo.listAll();
  return allPatients.filter(
    (patient) =>
      (patient.FirstName &&
        patient.FirstName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (patient.LastName &&
        patient.LastName.toLowerCase().includes(searchTerm.toLowerCase()))
  );
};

// Export the repository instance for direct access if needed
export { patientRepo as default };
